import { ReactElement } from "react";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";

import { MapLocation } from "./types";
import type { MapKit, MarkerAnnotation, CoordinateRegion } from "./mapkit";


function getMapKit(): MapKit | null {
  return (window as any).mapkit;
}


let loadingPromise: Promise<MapKit> | null = null;
export function loadMapKitScript(): Promise<MapKit> {
  if (loadingPromise) {
    return loadingPromise;
  }

  if (getMapKit()) {
    return Promise.resolve(getMapKit()!);
  }

  loadingPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js";
    script.async = true;

    script.onload = () => {
      if (getMapKit()) {
        resolve(getMapKit()!);
      } else {
        loadingPromise = null;
        reject(new Error("MapKit JS loaded but not available"));
      }
    };

    script.onerror = () => {
      loadingPromise = null;
      reject(new Error("Failed to load MapKit JS"));
    };

    document.head.appendChild(script);
  });

  return loadingPromise;
}

export function syncRender(child: ReactElement): HTMLElement {
  /*
    We'll handle converting React rendered content to plain HTML here.

    Mapkit requires creating content synchronously, so since React renders
    async we have to use flushSync.

    We don't return the root div element itself, the extra div wrapper
    throws off the Mapkit callout bubble styling.
  */
  const el = document.createElement("div");
  const root = createRoot(el);
  flushSync(() => {
    root.render(child);
  });
  return el.firstElementChild as HTMLElement;
};

export function calculateRegionForAnnotations(
  mapkitx: MapKit,
  annotations: Array<MarkerAnnotation>,
): CoordinateRegion | null {
  if (annotations.length === 0) {
    console.log("No annotations");
    return null;
  }

  if (annotations.length === 1) {
    return new mapkitx.CoordinateRegion(
      annotations[0].coordinate,
      new mapkitx.CoordinateSpan(0.01, 0.01),
    );
  }

  const coordinates = annotations.map((a) => a.coordinate);
  const latitudes = coordinates.map((c) => c.latitude);
  const longitudes = coordinates.map((c) => c.longitude);

  const minLat = Math.min(...latitudes);
  const maxLat = Math.max(...latitudes);
  const minLng = Math.min(...longitudes);
  const maxLng = Math.max(...longitudes);

  const centerLat = (minLat + maxLat) / 2;
  const centerLng = (minLng + maxLng) / 2;
  const latDelta = Math.abs(maxLat - minLat) * 1.3;
  const lngDelta = Math.abs(maxLng - minLng) * 1.3;

  return new mapkitx.CoordinateRegion(
    new mapkitx.Coordinate(centerLat, centerLng),
    new mapkitx.CoordinateSpan(latDelta, lngDelta),
  );
}

export function getAppleMapsURL(
  location: Partial<MapLocation>,
): string {
  const { latitude, longitude, title } = location;
  const address = location.data?.address as string;
  const params = new URLSearchParams({
    ll: `${latitude},${longitude}`,
  });

  const query = title || address;
  if (query) {
    params.set("q", query);
  }

  if (address && address !== query) {
    params.set("address", address);
  }

  return `https://maps.apple.com/?${params.toString()}`;
}
